// ─── Constants ───────────────────────────────────────────────────────────────

const roles = { admin: 1, manager: 2 };
const departments = { finance: 2, IT: 3 };

/** Employee IDs that get special menu access beyond their role. */
const privilegedEmployees = {
  management: new Set([17, 50, 94]),
  tpmk: new Set([42]),
};

// ─── Menu Definitions ─────────────────────────────────────────────────────────

const coreMenu = [
  {
    title: "Information Panel",
    icon: "HomeIcon",
    route: "Home",
  },
  {
    title: "Client Transactions",
    icon: "BookOpenIcon",
    children: [
      { title: "Client List", route: "Client" },
      { title: "Owner List", route: "Owner" },
      {
        title: "Reports",
        children: [
          { title: "Client Responsible Report", route: "ClientResponsibleReport" },
          { title: "Different Invoice Report", route: "ClientAccountingDifferentInvoiceReport" },
          { title: "Special Invoice Report", route: "ClientAccountingSpecialInvoiceReport" },
          { title: "Invoice Alert Day Report", route: "ClientAccountingInvoiceAlertDayReport" },
          { title: "Agent Transactions Report", route: "AgentTransactionsReport" },
        ],
      },
    ],
  },
  {
    title: "Design Transactions",
    icon: "CodepenIcon",
    children: [
      { title: "Design List", route: "Design" },
      { title: "Design Opposition List", route: "DesignOpposition" },
      {
        title: "Reports",
        children: [
          { title: "Deadline Report", route: "DesignDeadlineReport" },
          { title: "Bulletin Check Report", route: "DesignBulletinCheckReport" },
          { title: "Bulletin Analyse", route: "DesignBulletinAnalyseReport" },
          { title: "Design Renewal Report", route: "DesignRenewalReport" },
        ],
      },
    ],
  },
  {
    title: "Trademark Transactions",
    icon: "GridIcon",
    children: [
      { title: "Domain List", route: "Domain" },
      { title: "Domain Opposition", route: "DomainOpposition" },
      { title: "Trademark List", route: "Trademark" },
      { title: "Trademark Opposition List", route: "TrademarkOpposition" },
      { title: "Trademark Search", route: "TrademarkSearch" },
      {
        title: "Reports",
        children: [
          { title: "Trademark Transaction Report", route: "TrademarkTransactionReport" },
          { title: "Bulletin Analyse", route: "TrademarkBulletinAnalyseReport" },
          { title: "Trademark Bulletin Check", route: "TrademarkBulletinCheck" },
          { title: "Trademark Renewal Report", route: "TrademarkRenewalReport" },
          { title: "Trademark Objection To Us Report", route: "TrademarkObjectionToUsReport" },
        ],
      },
    ],
  },
  {
    title: "Patent Transactions",
    icon: "Share2Icon",
    children: [
      { title: "Patent List", route: "Patent" },
      {
        title: "Annuity Transactions",
        icon: "FileIcon",
        children: [
          { title: "Agent Annuity Price", route: "AgentAnnuityPrice" },
          { title: "Annuity", route: "Annuity" },
        ],
      },
      { title: "Patent Update Fields", route: "PatentUpdateFields" },
      {
        title: "Reports",
        children: [
          { title: "Patent Transaction Report", route: "PatentTransactionReport" },
          { title: "Bulletin Analyse", route: "PatentBulletinAnalyseReport" },
          { title: "Patent Bulletin Check", route: "PatentBulletinCheck" },
          { title: "Patent Certificate", route: "PatentCertificate" },
          { title: "Patent Working Reminder", route: "PatentWorkingReminder" },
          { title: "Patent Annual Fee Reminder", route: "PatentAnnualFeeReminder" },
          { title: "Patent Deadline Report", route: "PatentDeadlineReport" },
          { title: "Patent Official Deadline Report", route: "PatentOfficialDeadlineLogReport" },
        ],
      },
    ],
  },
  {
    title: "Plant Variety Transactions",
    icon: "FeatherIcon",
    children: [
      {
        title: "Plant Variety",
        route: "PlantVariety",
      },
    ]
  },
  {
    title: "National",
    icon: "GlobeIcon",
    children: [
      { title: "National Trademark List", route: "NationalTrademark" },
      { title: "National Trademark Search", route: "NationalTrademarkSearch" },
      { title: "National Patent List", route: "NationalPatent" },
    ],
  },
  {
    title: "Translator Transactions",
    icon: "MicIcon",
    children: [
      { title: "Translator List", route: "Translator" },
      { title: "Translate Report", route: "TranslateReport" },
    ],
  },
  {
    title: "External Services",
    icon: "ExternalLinkIcon",
    children: [
      { title: "Lexology", route: "Lexology" },
    ],
  },
  {
    title: "Reports",
    icon: "BarChartIcon",
    children: [
      { title: "Mail History", route: "MailHistory" },
    ],
  },
  {
    title: "Finance",
    icon: "DollarSignIcon",
    children: [
      { title: "Invoice", icon: "BookIcon", route: "Invoice" },
      { title: "OfficialFee", icon: "TrendingUpIcon", route: "OfficialFee" },
    ],
  },
];

const managementMenu = {
  title: "Management",
  icon: "BriefcaseIcon",
  children: [
    { title: "Budget List" }, // route intentionally omitted — not yet implemented
  ],
};

const tpmkMenu = {
  title: "TPMK",
  icon: "ChevronsUpIcon",
  children: [
    { title: "Etebs", route: "Etebs" },
  ],
};

const hrMenu = {
  title: "Human Resources",
  icon: "ThumbsUpIcon",
  children: [
    { title: "Employee", route: "Employee", icon: "UsersIcon" },
    { title: "EmploymentContract", route: "EmploymentContract", icon: "ClipboardIcon" },
    { title: "HourlyAnnualLeave", route: "EmployeeHourlyAnnualLeave", icon: "WatchIcon" },
    { title: "AnnualLeave", route: "EmployeeAnnualLeave", icon: "ClockIcon" },
    { title: "Overtime Work", route: "OvertimeWorkList", icon: "MoonIcon" },
    {
      title: "Reports",
      children: [
        { title: "Hourly Annual Leave Report", route: "HourlyAnnualLeaveReport" },
        { title: "Annual Leave Report", route: "AnnualLeaveReport" },
      ],
    },
  ],
};

const definitionsMenu = {
  title: "Definitions",
  icon: "ListIcon",
  children: [
    {
      title: "Operation",
      icon: "ActivityIcon",
      children: [
        { title: "Job Status Relation", route: "JobStatusRelation" },
        { title: "Job Status", route: "JobStatus" },
        { title: "Word Templates", route: "WordTemplates" },
        { title: "Document Types", route: "DocumentTypes" },
        { title: "Patent Note Type", route: "PatentNoteType" },
        { title: "Patent Annuity Will Pay", route: "PatentAnnuityWillPay" },
        { title: "Trademark Application Type", route: "TrademarkApplicationType" },
        { title: "National Trademark Application Type", route: "NationalTrademarkApplicationType" },
        { title: "National Patent Application Type", route: "NationalPatentApplicationType" },
        { title: "National Patent Authority", route: "NationalPatentAuthority" },
        { title: "National Patent Receiving Office", route: "NationalPatentReceivingOffice" },
        { title: "Trademark Type", route: "TrademarkType" },
        { title: "Trademark Search Database", route: "TrademarkSearchDatabase" },
        { title: "Trademark Search Success Rate", route: "TrademarkSearchSuccessRate" },
        { title: "Nice Class Type", route: "NiceClassType" },
        { title: "Trademark Search Type", route: "TrademarkSearchType" },
        { title: "Trademark Note Type", route: "TrademarkNoteType" },
        { title: "Patent Status", route: "PatentStatus" },
        { title: "Annuity Detail Status", route: "AnnuityDetailStatus" },
        { title: "Design Status", route: "DesignStatus" },
        { title: "Design Publication Type", route: "DesignPublicationType" },
        { title: "Patent Annuity Status", route: "PatentAnnuityStatus" },
        { title: "Trademark Status", route: "TrademarkStatus" },
        { title: "Trademark Search Status", route: "TrademarkSearchStatus" },
        { title: "Patent Search Database", route: "PatentSearchDatabase" },
        { title: "Trademark Objection To Opposing View Status", route: "TrademarkObjectionToOpposingViewStatus" },
        { title: "Trademark Objection To Us Case Decision", route: "TrademarkObjectionToUsCaseDecision" },
        { title: "Trademark Objection To Us Decision", route: "TrademarkObjectionToUsDecision" },
        { title: "Trademark Objection To Us Status", route: "TrademarkObjectionToUsStatus" },
        { title: "Trademark Objection To Us Yidk Status", route: "TrademarkObjectionToUsYidkStatus" },
        { title: "Trademark Provisional Case Instruction", route: "TrademarkProvisionalCaseInstruction" },
        { title: "Trademark Provisional Status", route: "TrademarkProvisionalStatus" },
        { title: "Trademark Opposition Status", route: "TrademarkOppositionStatus" },
        { title: "National Trademark Search Status", route: "NationalTrademarkSearchStatus" },
        { title: "Trademark Publication Type", route: "TrademarkPublicationType" },
        { title: "Domain Status", route: "DomainStatus" },
        { title: "Plant Variety Status", route: "PlantVarietyStatus", },
        { title: "Plant Variety Annuity Status", route: "PlantVarietyAnnuityStatus", },
        { title: "Plant Variety Annuity Will Pay", route: "PlantVarietyAnnuityWillPay", },
        { title: "Plant Variety Note Type", route: "PlantVarietyNoteType", },
        { title: "National Trademark Note Type", route: "NationalTrademarkNoteType" },
      ],
    },
    {
      title: "Finance",
      icon: "ApertureIcon",
      children: [
        { title: "Note To Be On Invoice", route: "NoteToBeOnInvoice" },
        { title: "Price Lists", route: "PriceLists" },
        { title: "Invoice Header", route: "InvoiceHeader" },
        { title: "Invoice Status", route: "InvoiceStatus" },
        { title: "Billing Status", route: "BillingStatus" },
        { title: "Ledes Code Type", route: "LedesCodeType" },
        { title: "Ledes Type", route: "LedesType" },
        { title: "Service Types", route: "ServiceTypes" },
        { title: "Services", route: "Services" },
        { title: "ServiceFees", route: "ServiceFees" },
        { title: "Credit Card", route: "CreditCard" },
        { title: "Client Accounting Category", route: "ClientAccountingCategory" },
        { title: "Invoice Discount Format", route: "InvoiceDiscountFormat" },
        { title: "Bank", route: "Bank" },
        { title: "Business Partner Type", route: "BusinessPartnerType" },
        { title: "Official Fee Payment Institution", route: "OfficialFeePaymentInstitution" },
        { title: "E-Billing Portal", route: "EBillingPortal" },
        { title: "E-Billing File Type", route: "EBillingFileType" },
        { title: "Activity", route: "Activity" },
      ],
    },
    {
      title: "Human Resources",
      icon: "GlobeIcon",
      children: [
        { title: "Departments", route: "Departments" },
        { title: "Permission Template", route: "PermissionTemplate" },
        { title: "EmploymentContractType", route: "EmploymentContractType" },
        { title: "Annual Leave Type", route: "AnnualLeaveType" },
        { title: "Positions", route: "Positions" },
        { title: "Titles", route: "Titles" },
        { title: "Liable Fields", route: "LiableFields" },
        { title: "Holidays", route: "Holidays" },
        { title: "Overtime Work Reason", route: "OvertimeWorkReason" },
        { title: "Overtime Work Type", route: "OvertimeWorkType" },
      ],
    },
    { title: "Mail Account", route: "MailAccount", icon: "MailIcon" },
    { title: "Mail Template", route: "MailTemplate", icon: "VoicemailIcon" },
    { title: "Employee Classification", route: "EmployeeClassification", icon: "UserPlusIcon" },
    { title: "Country", route: "Country", icon: "MapIcon" },
    { title: "State", route: "State", icon: "FlagIcon" },
    { title: "Contact Types", route: "ContactTypes", icon: "PhoneIncomingIcon" },
    { title: "Mail Contact Types", route: "MailContactTypes", icon: "MessageSquareIcon" },
    { title: "Invoice Send Types", route: "InvoiceSendTypes", icon: "ShoppingBagIcon" },
    { title: "Responsible Fields", route: "ResponsibleFields", icon: "FilmIcon" },
    { title: "Languages", route: "Languages", icon: "GlobeIcon" },
    { title: "Sectors", route: "Sectors", icon: "GlobeIcon" },
    { title: "Note Type", route: "NoteType", icon: "Edit2Icon" },
    { title: "Mail To Read", route: "MailToRead", icon: "MailIcon" },
    { title: "Purpose of Meet", route: "PurposeOfMeet", icon: "Link2Icon" },
  ],
};

const developersMenu = {
  title: "Developers",
  icon: "CodeIcon",
  children: [
    { title: "Permission Group", route: "PermissionGroup", icon: "BriefcaseIcon" },
    { title: "Permission", route: "Permission", icon: "LockIcon" },
    { title: "Time Type", route: "TimeType", icon: "ClockIcon" },
    { title: "Job Reminder Criterion", route: "JobReminderCriterion", icon: "RewindIcon" },
    { title: "Annual Leave Right", route: "AnnualLeaveRight", icon: "TagIcon" },
    { title: "Annual Leave Status", route: "AnnualLeaveStatus", icon: "BookIcon" },
    { title: "Bulletin Upload", route: "BulletinUpload", icon: "TargetIcon" },
  ],
};

const adminMenu = {
  title: "Admin",
  icon: "UnlockIcon",
  children: [
    { title: "Admin Hourly Annual Leave", route: "AdminEmployeeHourlyAnnualLeave", icon: "WatchIcon" },
    { title: "Admin Annual Leave", route: "AdminEmployeeAnnualLeave", icon: "ClockIcon" },
    { title: "Admin Overtime Work", route: "OvertimeWorkListForManager", icon: "MoonIcon" },
    { title: "Change Two Factor Authentication", route: "ChangeTwoFactorAuth", icon: "LockIcon" },
  ],
};

const twoFactorOnlyAdminMenu = {
  title: "Admin",
  icon: "UnlockIcon",
  children: [
    { title: "Change Two Factor Authentication", route: "ChangeTwoFactorAuth", icon: "LockIcon" },
  ],
};

const accountingMenu = {
  title: "Accounting",
  icon: "BarChartIcon",
  children: [
    { title: "Payment", route: "Payment" },
    { title: "TranslatorPayments", route: "TranslatorPayments" },
    { title: "Ledes", route: "Ledes" },
    { title: "PO Numbers", route: "PoNumber" },
    { title: "InvoiceApproval", route: "InvoiceApproval" },
    { title: "Create EInvoice", route: "CreateEInvoice" },
    { title: "Create Kktc EInvoice", route: "CreateKktcEInvoice" },
    { title: "EInvoice", route: "EInvoice" },
    { title: "AccountingBooks", route: "AccountingBooks" },
    { title: "Statement", route: "Statement" },
    { title: "InvoiceAgingReport", route: "InvoiceAgingReport" },
    { title: "EndOfTheDay", route: "EndOfTheDay", icon: "TrendingDownIcon" },
    { title: "InvoiceDetailReport", route: "InvoiceDetailReport" },
    { title: "Client Average Payment Duration Report", route:"ClientAveragePaymentDurationReport"}
  ],
};

// ─── Helper ───────────────────────────────────────────────────────────────────

/** Deep-clone an object so callers can't mutate the shared definitions above. */
const clone = (obj) => JSON.parse(JSON.stringify(obj));

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function getMenu() {
  const roleId = parseInt(localStorage.getItem("RoleId"), 10);
  const departmentId = parseInt(localStorage.getItem("DepartmentId"), 10);
  const employeeId = parseInt(localStorage.getItem("EmployeeId"), 10);

  const isAdmin = roleId === roles.admin;
  const isManager = roleId === roles.manager;
  const isFinance = departmentId === departments.finance;
  const isIT = departmentId === departments.IT;

  const menu = clone(coreMenu);

  // ── Management (admins + specific employees) ────────────────────────────────
  if (isAdmin || privilegedEmployees.management.has(employeeId)) {
    menu.push(clone(managementMenu));
  }

  // ── TPMK (admins + specific employee) ───────────────────────────────────────
  if (isAdmin || privilegedEmployees.tpmk.has(employeeId)) {
    menu.push(clone(tpmkMenu));
  }
  // ── HR & Definitions (admins and managers) ──────────────────────────────────
  if (isAdmin || isManager) {
    menu.push(clone(hrMenu), clone(definitionsMenu));
  }

  // ── Developer tools (admins only) ───────────────────────────────────────────
  if (isAdmin) {
    menu.push(clone(developersMenu), clone(adminMenu));
  } else if (isIT) {
    menu.push(clone(twoFactorOnlyAdminMenu));
  }

  // ── Accounting inside Finance (admins and finance department) ───────────────
  if (isAdmin || isFinance) {
    const financeMenu = menu.find((item) => item.title === "Finance");
    financeMenu?.children.push(clone(accountingMenu));
  }

  return menu;
}